/**
 * Keeps a section's markup out of the page load, and puts it in the document as soon as
 * the browser is free again.
 *
 * Sections opt in with `defer_until_visible` (sections/_blocks.liquid), which parks their
 * blocks in a <template>. Nothing inside a <template> is in `document`: it isn't matched
 * by `querySelectorAll`, its scripts are never prepared, its images are never fetched. An
 * app block that boots itself by scanning the DOM therefore stays completely quiet until
 * this element hands it over.
 *
 * Written for Judge.me's review widget (BAS-326), whose loader costs a PDP 85 requests and
 * ~450 ms of main-thread work for a widget below the fold. See `sizeJudgeMeSkeleton` and
 * `reinitJudgeMe` for the two app-specific parts.
 *
 * Two triggers, whichever comes first, and the distinction between them is the whole
 * design: the cost this exists to avoid is a *load-time* cost, not a cost per se, so the
 * content is handed over on the first idle after `load` — long before anyone has scrolled
 * to it, and out of the way of everything the shopper is actually looking at. Approach is
 * the backstop, for a browser with no idle callback and for a page that is scrolled before
 * it has finished loading.
 *
 * Waiting for approach alone is what the first version did, and it is not enough: an app
 * that takes a second to restyle the markup it was handed makes that second visible, as
 * half-drawn content the shopper scrolls straight into. One viewport of lead is about a
 * second of scrolling, so the two were neck and neck. Idle turns a race into a head start.
 */
class DeferredSection extends HTMLElement {
  connectedCallback() {
    this.#template = this.querySelector('[data-deferred-section-content]');
    this.#sectionId = this.dataset.sectionId || null;
    // Parked sections carry their markup; fetched ones carry only the id to ask for.
    // Neither means there is nothing to do later, so do nothing now.
    if (!this.#template && !this.#sectionId) return;

    // Content that is only ever seen inside a drawer (the cart drawer's recommendations)
    // waits for that drawer to open — neither idle nor approach means anything for a closed
    // dialog, and loading it on every page would spend a request on a pane most visits
    // never show. `[open]` covers a drawer restored open before this module loaded.
    if (this.dataset.trigger === 'drawer') {
      const drawer = this.closest('theme-drawer');
      if (drawer) {
        if (drawer.hasAttribute('open')) this.reveal();
        else drawer.addEventListener('theme-drawer:open', () => this.reveal(), { once: true });
        return;
      }
    }

    this.#revealWhenIdle();

    // No IntersectionObserver (or a crawler that reports none): reveal immediately rather
    // than leave the section permanently empty.
    if (!('IntersectionObserver' in window)) {
      this.reveal();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.reveal();
      },
      {
        // Whatever is actually scrolling, which is not the same at every width: at
        // desktop widths `.page-wrapper` is `overflow-y: auto` and scrolls, below
        // 1024px it goes back to `visible` and the document scrolls.
        //
        // It has to be the real one, in both directions. Against the implicit viewport
        // root, `.page-wrapper`'s clipping ate the margin — `rootMargin` only grows the
        // root rectangle, and the result is then intersected with every clipping
        // ancestor — so sections revealed as they came on screen, too late to be ready
        // and late enough to shift what the shopper was looking at. Naming
        // `.page-wrapper` unconditionally broke the other end: where it isn't scrolling
        // its box is the full content height, so every section intersects it from the
        // start and nothing defers at all.
        root: scrollContainerOf(this),

        // One viewport of lead — about a second of scrolling — so the content has
        // landed by the time it is on screen. Relative rather than a pixel count so a
        // tall desktop and a phone each get a margin in proportion to how fast a scroll
        // crosses them. Two viewports reads better on paper but a PDP is only ~3,500 px
        // tall on a phone, so the deferred sections sit inside the margin from the
        // start and everything loads at once: the lead has to leave a gap on the
        // shortest page it runs on, or it isn't deferring anything there.
        rootMargin: this.dataset.rootMargin || '100% 0px',
      },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /**
   * Hands the content over on the first idle after the page has loaded.
   *
   * `load` rather than `DOMContentLoaded`: the point is to be behind everything the page
   * needs to finish arriving, and an idle callback scheduled before that would be spent
   * competing with the images the shopper is looking at. After it, the browser is doing
   * nothing anyway.
   *
   * The `timeout` is what makes this a promise rather than a hope — a page that never goes
   * idle (a carousel, an ad script) would otherwise keep the section parked until it is
   * scrolled to, which is exactly the case this is here to avoid. Without
   * `requestIdleCallback` at all (Safari until 2022), a plain timer stands in: less
   * considerate, same guarantee.
   *
   * `readyState` first, because a module script's element can be upgraded either side of
   * `load` depending on how long the rest of the page takes.
   */
  #revealWhenIdle() {
    const hand = () => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback(() => this.reveal(), { timeout: 1000 });
      else setTimeout(() => this.reveal(), 500);
    };

    if (document.readyState === 'complete') hand();
    else window.addEventListener('load', hand, { once: true });
  }

  #template = null;
  #sectionId = null;
  #observer = null;
  #revealed = false;

  /**
   * Puts the parked content in the document now.
   *
   * Public because approach is not the only reason to reveal: something jumping straight
   * to a deferred section - the rating stars beside the product title, which travel to
   * the review widget (assets/review-badge-jump.js) - has to bring it with it. Calling it
   * a second time is a no-op, so a jump and the observer racing each other is fine.
   */
  async reveal() {
    if (this.#revealed) return;
    this.#revealed = true;
    this.#observer?.disconnect();

    const content = this.#template ? this.#template.content.cloneNode(true) : await this.#fetchContent();

    // The fetch failed. Re-arm rather than leave the section permanently empty: the
    // observer gets another go if the shopper scrolls back past it, which is the moment
    // it matters most.
    if (!content) {
      this.#revealed = false;
      this.#observer?.observe(this);
      // A drawer-triggered one has no observer: try again on the drawer's next open.
      if (this.dataset.trigger === 'drawer') {
        this.closest('theme-drawer')?.addEventListener('theme-drawer:open', () => this.reveal(), { once: true });
      }
      return;
    }

    // A cloned <script> keeps the original's "already started" flag, and a template's
    // scripts were never started — so in principle these would run on insertion. In
    // principle: the rule is subtle enough, and the cost of getting it wrong (a widget
    // with no data) high enough, that the scripts are rebuilt as fresh elements instead.
    for (const original of content.querySelectorAll('script')) {
      const script = document.createElement('script');
      for (const { name, value } of original.attributes) script.setAttribute(name, value);
      script.textContent = original.textContent;
      original.replaceWith(script);
    }

    this.#template?.remove();
    this.appendChild(content);
    this.#sizeJudgeMeSkeleton();

    this.dispatchEvent(new CustomEvent('deferred-section:revealed', { bubbles: true }));
    this.#reinitJudgeMe();
  }

  /**
   * Asks the Section Rendering API for this section, and answers its content.
   *
   * The URL is the page the shopper is on, so the section renders in the context it was
   * configured for — the same product, the same template, the same market.
   *
   * The response is a whole section, wrapper and all, and this element already stands
   * inside that wrapper on the page. So what is wanted is not the response but the part
   * of it the Liquid side marked: rendered alone, the snippet hands back the same
   * `<template data-deferred-section-content>` the parked strategy would have written, and
   * one code path reads both.
   *
   * @returns {Promise<DocumentFragment | null>} null if the section could not be fetched.
   */
  async #fetchContent() {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.#sectionId);
      // A section is a fragment of this page, not a page - keep it out of the back/forward
      // cache and out of any Link-header preloading meant for documents.
      const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) return null;

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const template = parsed.querySelector('template[data-deferred-section-content]');
      if (!(template instanceof HTMLTemplateElement)) return null;

      // Across documents: the fragment belongs to the parsed document until it is adopted.
      return document.importNode(template.content, true);
    } catch {
      return null;
    }
  }

  /**
   * Tells the skeleton base.css draws in the review widget's place how many reviews the
   * widget will open with, as `--jdgm-review-count`, so it stands in at close to the real
   * height.
   *
   * Judge.me's own data rather than a count of the markup: the crawler copy the skeleton
   * replaces lists every review, while the widget's first page holds a page of reviews in
   * the shopper's language and a smaller one in any other. That data is an inline script
   * inside the section, so it has only just run, on insertion, and nothing has painted
   * since: the skeleton's first frame is already the right height.
   *
   * Only for data in the per-language shape. Anything else leaves the property unset and
   * the skeleton at its one-row floor, which is short, not wrong.
   */
  #sizeJudgeMeSkeleton() {
    const data = window.jdgm?.data?.reviewWidget;
    if (!data) return;

    for (const widget of this.querySelectorAll('.jdgm-review-widget[data-product-id]')) {
      if (!(widget instanceof HTMLElement)) continue;

      const reviews = data[widget.dataset.productId ?? ''];
      if (!Array.isArray(reviews?.primary_language_reviews)) continue;

      const count =
        firstPageLength(reviews.primary_language_reviews, reviews.primary_language_pagination) +
        firstPageLength(reviews.other_language_reviews, reviews.other_language_pagination);
      widget.style.setProperty('--jdgm-review-count', String(count));
    }
  }

  /**
   * Judge.me's loader runs once, scans for `.jdgm-widget[data-entry-point]` and returns
   * early when it finds none — which is precisely what it found while our widget sat in
   * the template. It exposes no rescan, so the way back in is its own entry point: clear
   * the re-entry guard it sets and load the same (now cached) script again.
   *
   * Deliberately narrow. If any of these assumptions stop holding, the widget's markup is
   * in the document either way and the worst case is that it renders unstyled, not that
   * the page breaks.
   */
  #reinitJudgeMe() {
    if (!window.jdgm) return;
    if (!this.querySelector('.jdgm-widget[data-entry-point]')) return;

    const loader = document.querySelector('script[src*="judgeme"][src*="loader.js"]');
    if (!loader) return;

    window.jdgm._loaderExecuted = false;
    const rerun = document.createElement('script');
    rerun.src = loader.src;
    document.head.appendChild(rerun);
  }
}

/**
 * The nearest ancestor that actually scrolls, or null for the viewport — which is what
 * IntersectionObserver wants for "the document scrolls".
 *
 * An element only counts when it both declares a scrolling overflow and has something to
 * scroll: `.page-wrapper` keeps its box either way, so the overflow value alone would
 * still name it at mobile widths where it is `visible`.
 *
 * @param {Element} element
 * @returns {Element | null}
 */
function scrollContainerOf(element) {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (/auto|scroll|overlay/.test(overflowY) && node.scrollHeight > node.clientHeight) return node;
  }
  return null;
}

/**
 * How many of `reviews` Judge.me's widget shows on its first page.
 *
 * The lists already arrive one page long. The cap is for one that ever arrives longer:
 * counting past a page would size the skeleton for reviews the widget won't show, and
 * make it taller than the widget it stands in for.
 *
 * @param {unknown} reviews
 * @param {{ per_page?: unknown } | undefined} pagination
 * @returns {number}
 */
function firstPageLength(reviews, pagination) {
  if (!Array.isArray(reviews)) return 0;

  const perPage = pagination?.per_page;
  return typeof perPage === 'number' ? Math.min(reviews.length, perPage) : reviews.length;
}

if (!customElements.get('deferred-section')) {
  customElements.define('deferred-section', DeferredSection);
}

/**
 * Finds the content matching `selector` wherever it currently is, revealing the section
 * holding it if it is still parked, and answers the element to travel to.
 *
 * For anything that jumps straight to deferred content rather than scrolling down into
 * it. A caller says what it is looking for and gets somewhere to go, without having to
 * know whether the section it wants was deferred at all: content already in the document
 * answers itself.
 *
 * A parked section answers with the `<deferred-section>` rather than the content, which
 * is only a clone away from being in the document at that point and has no box of its own
 * to aim at yet. The element stands exactly where its content will, so it is the right
 * thing to travel to either way.
 *
 * `customElements.upgrade` because this module defining the element is not the same as
 * every instance having been upgraded: a section still waiting its turn would have no
 * `reveal` to call, and would be left to its own observer for no reason.
 *
 * @param {string} selector
 * @returns {Element | null}
 */
export function revealDeferredContent(selector) {
  const present = document.querySelector(selector);
  if (present) return present;

  for (const section of document.querySelectorAll('deferred-section')) {
    // A fetched section has nothing to search yet - its markup is still on the server.
    // Sections whose content something jumps to should use the parked strategy for
    // exactly this reason; see the note in `snippets/deferred-section.liquid`.
    const template = section.querySelector('template[data-deferred-section-content]');
    if (!(template instanceof HTMLTemplateElement)) continue;
    if (!template.content.querySelector(selector)) continue;

    customElements.upgrade(section);
    if (section instanceof DeferredSection) section.reveal();

    return section;
  }

  return null;
}
